import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import moment from 'moment'

import { displayWithCurrency } from '../../../utility/currency'
import { invoiceRefundAmount, invoiceOfflinePaymentDescription } from '../../../utility/enrollmentsBilling'
import Modal from '../../common/Modal'
import SubmitButton from '../../common/buttons/SubmitButton'
import ProfileAvatar from '../../common/avatar/ProfileAvatar'
import Input from '../../common/form/Input'
import { ReactComponent as InfoIcon } from '../../../assets/images/common/icons/info-circle-icon-dark.svg'

import classes from './RefundInvoiceModal.module.scss'

const money = (amount, currency) => (currency ? displayWithCurrency(amount, currency) : `$${Number(amount || 0).toFixed(2)}`)

// Read-only values derived from the invoice/enrollment (kept out of the component to bound complexity).
const deriveRefundInfo = ({ invoice, enrollment, t }) => {
  const isOnline = invoice ? invoice.paymentMethod === 'online' : enrollment?.paymentStatus === 'paid_online'
  const course = enrollment?.course
  return {
    isOnline,
    course,
    student: enrollment?.student,
    familyAdminEmail: enrollment?.student?.familyAdmins?.[0]?.email,
    currency: (invoice?.currencyCode && { code: invoice.currencyCode }) || enrollment?.currency || null,
    amountPaid: Number(invoice?.total ?? enrollment?.transactionAmount ?? 0),
    alreadyRefunded: invoiceRefundAmount(invoice),
    paymentMethodLabel: isOnline
      ? t('enrollmentsBilling.refundModal.paymentOnlineStripe')
      : t('enrollmentsBilling.refundModal.paymentOffline', {
        description: invoiceOfflinePaymentDescription(invoice) || t('enrollmentsBilling.refundModal.unspecified'),
      }),
    programDates: course?.startDate
      ? `${moment(course.startDate).format('MM/DD/YYYY')}${course.endDate ? `-${moment(course.endDate).format('MM/DD/YYYY')}` : ''}`
      : '-',
  }
}

// Drop/Refund is three choices: drop only, refund only, or both, so the amount only matters when a
// refund is actually selected. The plain "refund" mode always refunds.
const deriveRefundChoice = ({ isDropRefund, shouldDrop, shouldRefund, amount, maxRefund, isLoading }) => {
  const wantsRefund = isDropRefund ? shouldRefund : true
  const refundNow = wantsRefund ? Number(amount || 0) : 0
  const nothingSelected = isDropRefund && !shouldDrop && !shouldRefund
  const amountInvalid = wantsRefund && !(refundNow > 0 && refundNow <= maxRefund)

  return { wantsRefund, refundNow, submitDisabled: isLoading || nothingSelected || amountInvalid }
}

// Refund modal (Figma 1977-6692). Two modes:
//   refund      — Billing-tab "Refund": partial/full refund on a paid invoice (online or offline).
//   dropRefund  — Enrollment-tab "Drop/Refund": drops the enrollment; the refund is issued in full
//                 server-side (paid-online only), so the amount is shown read-only here.
// Two columns: left form (Stripe-fee notice for online, amount, custom message, send email),
// right billing card (Amount Paid / Refunded Amount / Net Amount).
const RefundInvoiceModal = ({ isOpen, onClose, onSubmit, isLoading, invoice, enrollment, mode }) => {
  const { t } = useTranslation()
  const isDropRefund = mode === 'dropRefund'
  const {
    isOnline, course, student, familyAdminEmail, currency, amountPaid, alreadyRefunded, paymentMethodLabel, programDates,
  } = deriveRefundInfo({ invoice, enrollment, t })
  const maxRefund = Math.max(amountPaid - alreadyRefunded, 0)

  const [amount, setAmount] = useState(maxRefund > 0 ? maxRefund.toFixed(2) : '0')
  const [coachMessage, setCoachMessage] = useState('')
  const [sendEmail, setSendEmail] = useState(false)
  // Drop/Refund is three choices: drop only, refund only, or both. Refund-only leaves the
  // enrollment active and just refunds the invoice.
  const [shouldDrop, setShouldDrop] = useState(true)
  const [shouldRefund, setShouldRefund] = useState(true)

  const { wantsRefund, refundNow, submitDisabled } = deriveRefundChoice({
    isDropRefund, shouldDrop, shouldRefund, amount, maxRefund, isLoading,
  })
  const netAmount = Math.max(amountPaid - alreadyRefunded - refundNow, 0)

  const handleSubmit = () => onSubmit({
    refundAmount: refundNow,
    notes: coachMessage,
    sendEmail,
    dropEnrollment: isDropRefund ? shouldDrop : false,
    issueRefund: wantsRefund,
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} className={classes.modal}>
      <div className={classes.layout}>
        {/* Left: form */}
        <div className={classes.form}>
          <h2 className={classes.heading}>
            {isDropRefund ? t('enrollmentsBilling.refundModal.dropRefundTitle') : t('enrollmentsBilling.refundModal.issueRefundTitle')}
          </h2>
          {isDropRefund && (
            <p className={classes.subheading}>{t('enrollmentsBilling.refundModal.dropRefundSubheading')}</p>
          )}

          {isOnline && (
            <div className={classes.stripeBanner}>
              <span>{t('enrollmentsBilling.refundModal.stripeFeesNotice')}</span>
              <InfoIcon className={classes.infoIcon} />
            </div>
          )}

          {isDropRefund && (
            <>
              <label className={classNames(classes.checkRow, classes.tightRow)}>
                <input
                  className={classes.nativeInput}
                  type='checkbox'
                  checked={shouldDrop}
                  onChange={e => setShouldDrop(e.target.checked)}
                />
                <span className={classes.checkbox} />
                <span>{t('enrollmentsBilling.refundModal.dropEnrollment')}</span>
              </label>
              <label className={classNames(classes.checkRow, classes.tightRow)}>
                <input
                  className={classes.nativeInput}
                  type='checkbox'
                  checked={shouldRefund}
                  onChange={e => setShouldRefund(e.target.checked)}
                />
                <span className={classes.checkbox} />
                <span>{t('enrollmentsBilling.refundModal.issueRefund')}</span>
              </label>
            </>
          )}

          {wantsRefund && (
            <>
              <span className={classes.label}>{t('enrollmentsBilling.refundModal.refundedAmount')}</span>
              <Input name='refundAmount' type='number' value={amount} onChange={e => setAmount(e.target.value)} />
              <p className={classes.hint}>{t('enrollmentsBilling.refundModal.refundUpTo', { amount: money(maxRefund, currency) })}</p>
            </>
          )}

          <span className={classes.label}>{t('enrollmentsBilling.refundModal.addCustomMessage')}</span>
          <textarea
            className={classes.textarea}
            value={coachMessage}
            onChange={e => setCoachMessage(e.target.value)}
            placeholder={t('enrollmentsBilling.refundModal.customMessagePlaceholder')}
          />

          <label className={classes.checkRow}>
            <input className={classes.nativeInput} type='checkbox' checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
            <span className={classes.checkbox} />
            <span>{t('enrollmentsBilling.refundModal.sendAsEmail')}</span>
          </label>
        </div>

        {/* Right: billing card */}
        <div className={classes.preview}>
          <div className={classes.invoiceCard}>
            {student && (
              <div className={classes.studentRow}>
                <ProfileAvatar student={student} avatarClassName='small-round-image-wrapper' />
                <span className={classes.studentName}>{student.fullName || `${student.firstName} ${student.lastName}`}</span>
              </div>
            )}

            <span className={classes.sectionTitle}>{t('enrollmentsBilling.refundModal.billingDetails')}</span>

            <div className={classes.detailRows}>
              <Row label={t('enrollmentsBilling.refundModal.program')} value={course?.name || '-'} />
              <Row label={t('enrollmentsBilling.refundModal.facility')} value={course?.communityName || '-'} />
              <Row label={t('enrollmentsBilling.refundModal.coach')} value={course?.coachName || '-'} />
              {familyAdminEmail && <Row label={t('enrollmentsBilling.refundModal.familyAdminEmail')} value={familyAdminEmail} />}
              <Row label={t('enrollmentsBilling.refundModal.programDates')} value={programDates} />
              <Row label={t('enrollmentsBilling.refundModal.paymentMethod')} value={paymentMethodLabel} valueClass={classes.paymentMethodValue} />
            </div>

            <div className={classes.amountsBox}>
              <Row label={t('enrollmentsBilling.refundModal.amountPaid')} value={money(amountPaid, currency)} boldLabel />
              <Row
                label={t('enrollmentsBilling.refundModal.refundedAmount')}
                value={money(alreadyRefunded + refundNow, currency)}
                boldLabel
                valueClass={classes.refundValue}
              />
            </div>

            <div className={classes.totalRow}>
              <span>{t('enrollmentsBilling.refundModal.netAmount')}</span>
              <span>{money(netAmount, currency)}</span>
            </div>

            {coachMessage && (
              <>
                <span className={classes.sectionTitle}>{t('enrollmentsBilling.refundModal.additionalNotes')}</span>
                <p className={classes.notesText}>{coachMessage}</p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <SubmitButton
          buttonMessage={t('enrollmentsBilling.refundModal.confirmChanges')}
          buttonColor='orange-button'
          className={classes.submitButton}
          disabled={submitDisabled}
          loading={isLoading}
          onClick={handleSubmit}
        />
      </div>
    </Modal>
  )
}

const Row = ({ label, value, valueClass, boldLabel }) => (
  <div className={classes.detailRow}>
    <span className={classNames(classes.detailLabel, { [classes.boldLabel]: boldLabel })}>{label}</span>
    <span className={classNames(classes.detailValue, valueClass)}>{value}</span>
  </div>
)

Row.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  valueClass: PropTypes.string,
  boldLabel: PropTypes.bool,
}

RefundInvoiceModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  invoice: PropTypes.object,
  enrollment: PropTypes.object,
  mode: PropTypes.oneOf(['refund', 'dropRefund']),
}

RefundInvoiceModal.defaultProps = {
  mode: 'refund',
}

export default RefundInvoiceModal
